import React from 'react';
import { Section, SectionHeading, Button, CircleFigure } from './components/primitives';
import { EcosystemDiagram } from './components/EcosystemDiagram';
import { FlywheelDiagram, FLYWHEEL } from './components/FlywheelDiagram';
import { MultiplierRow } from './components/MultiplierRow';
import { SiteHeader } from './components/SiteHeader';
import { SiteFooter } from './components/SiteFooter';
import { SCENE_IMG, PORTRAIT } from './media';
import { ESSENTIALS, AUDIENCE_CARDS, CONTACT_HREF } from './content';
export function App() {
  return <div className="bg-background text-foreground" data-id="element-0" data-mp="2mjpvbd7vxaer">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-primary focus:px-5 focus:py-3 focus:text-[11px] focus:font-semibold focus:uppercase focus:tracking-label focus:text-primary-foreground" data-id="element-1" data-mp="31ayqs2ygxpzt">
        Skip to content
      </a>

      <SiteHeader data-id="element-2" data-mp="23v2r0wxj8p28" />

      <main id="main" data-id="element-3" data-mp="23ti7s3go4sjo">
        {/* HERO ------------------------------------------------------------ */}
        <Section id="top" size="xs" innerClassName="grid items-center gap-6 lg:grid-cols-[0.95fr_1.05fr]" data-id="element-4" data-mp="3mlja0y7k5j4n">
          <div className="relative z-10" data-id="element-5" data-mp="3rc9i4j7dc031">
            <SectionHeading as="h1" scale="display" eyebrow="A healthier, more abundant world" title={<>
                  Building
                  <br data-id="element-7" data-mp="2zmo6bandcp36" />
                  a world of
                  <br data-id="element-8" data-mp="33mf623uvifju" />
                  vitality.
                </>} data-id="element-6" data-mp="3rdkie91luppe" />
            <p className="mt-7 max-w-md text-body leading-body text-muted-foreground" data-id="element-9" data-mp="38m1fe27kzbnd">
              Vitality Foundation helps founders turn nature-inspired breakthroughs into companies that
              can change the world.
            </p>
            <p className="mt-4 max-w-md text-body leading-body text-muted-foreground" data-id="element-10" data-mp="nv0z3w0wmu6q">
              We connect them with expert mentors, first customers, non-dilutive funding, and a community
              of founders whose technologies can strengthen one another.
            </p>
            <p className="mt-5 max-w-md text-[11px] font-semibold uppercase tracking-overline" data-id="element-11" data-mp="3ubrgkhvzwc2x">
              Air &middot; Water &middot; Food &middot; Health &middot; Materials
            </p>
            <div className="mt-6" data-id="element-12" data-mp="85b8qvy8gczt">
              <Button href={CONTACT_HREF} data-id="element-13" data-mp="86clr5yt79k6">Get in touch</Button>
            </div>
          </div>
          <EcosystemDiagram data-id="element-14" data-mp="3glnzs6fvxakv" />
        </Section>

        {/* FIVE ESSENTIALS -------------------------------------------------- */}
        <Section id="essentials" size="md" bordered aria-labelledby="essentials-heading" data-id="element-15" data-mp="3qb7hv9vrx0eo">
          <div className="grid gap-6 lg:grid-cols-[0.7fr_1.3fr_auto] lg:items-start" data-id="element-16" data-mp="3qd0tl3l8uatj">
            <h2 id="essentials-heading" className="font-serif text-h2" data-id="element-17" data-mp="3liedwo84pw3n">
              The essentials
              <br data-id="element-18" data-mp="2xssfi2zkzkwp" />
              sustain us all.
            </h2>
            <p className="max-w-xl text-body-sm leading-body text-muted-foreground" data-id="element-19" data-mp="2sv0odb6rg2tk">
              A healthier, more abundant world depends on the systems that make life possible, and on the
              innovations that strengthen them. We support nature-inspired solutions across five essential
              systems.
            </p>
            <a href="#how" className="text-[11px] font-semibold uppercase tracking-label hover:text-primary" data-id="element-20" data-mp="ydqvdworkb3a">
              Explore the five essentials &rarr;
            </a>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-8 md:grid-cols-5" data-id="element-21" data-mp="1r2682ckfahbg">
            {ESSENTIALS.map(item => <CircleFigure key={item.name} src={item.img} alt="" label={item.name} headline={item.line} body={item.text} data-id="element-22" data-mp="1w0zks2rvy4ak" />)}
          </div>
        </Section>

        {/* MISSION ---------------------------------------------------------- */}
        <section id="mission" className="bg-background" aria-labelledby="mission-heading" data-id="element-23" data-mp="arkfko93lr2i">
          <div className="section-shell py-14 lg:py-16" data-id="element-24" data-mp="qxxcbac3io1a">
            <SectionHeading id="mission-heading" eyebrow="The big idea" title={<>
                  Innovation grows
                  <br data-id="element-26" data-mp="h4znynkfa4ur" />
                  when you connect it.
                </>} data-id="element-25" data-mp="m6p1c9v4aum0" />

            {/* Image and copy share a row beneath the header: one idea catching
                the light, next to what we do with it. */}
            <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-center" data-id="element-27" data-mp="14xyequ80e3iv">
              <figure className="relative m-0 overflow-hidden" data-id="element-28" data-mp="zxals307zcm0">
                <img src={SCENE_IMG.aspenLeaf} alt="A single golden aspen leaf backlit by the sun against a blue sky" className="block h-72 w-full object-cover object-center lg:h-[26rem]" data-id="element-29" data-mp="zzoh8cytaqu3" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2" style={{
                backgroundImage: 'linear-gradient(0deg, rgba(0,31,15,0.78) 0%, rgba(0,31,15,0) 100%)'
              }} data-id="element-30" data-mp="3gloam9e50b5z" />
                <figcaption className="absolute bottom-6 left-6 max-w-60 text-[11px] font-semibold uppercase leading-loose tracking-overline text-cream lg:left-8" data-id="element-31" data-mp="254blq7da3e9o">
                  Inspiration in every system.
                  <br data-id="element-32" data-mp="2eueqf4trignu" />
                  Possibility in every connection.
                </figcaption>
              </figure>

              <p className="max-w-[52ch] text-body leading-body text-muted-foreground" data-id="element-33" data-mp="2jvub2vs12l5p">
                We do not just support individual founders. We connect their ideas, technologies and
                capabilities to mentors, customers, partners, and one another. Because one founder's
                breakthrough could be exactly what another needs.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS ------------------------------------------------------ */}
        <Section id="how" size="md" aria-labelledby="how-heading" data-id="element-34" data-mp="1wshb64wiu01o">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-35" data-mp="1wubwhkbpfu6a">
            <div data-id="element-36" data-mp="2atbjyonhe30v">
              <h2 id="how-heading" className="font-serif text-h2" data-id="element-37" data-mp="2attqqf7rrzn1">
                From ideas
                <br data-id="element-38" data-mp="25se109und4yb" />
                to entirely new
                <br data-id="element-39" data-mp="2kq6hp5bxjze3" />
                possibilities.
              </h2>
              <p className="mt-6 max-w-sm text-body-sm leading-body text-muted-foreground" data-id="element-40" data-mp="2kobtni5d3r5s">
                When different innovations come together, they can unlock solutions none of them could
                build alone. This is the Vitality multiplier.
              </p>
            </div>
            <MultiplierRow data-id="element-41" data-mp="2phy7ver6c33p" />
          </div>

          <div className="mt-20 grid items-center gap-16 border-t border-border pt-16 lg:grid-cols-2" data-id="element-42" data-mp="33gei3fo8wbql">
            <FlywheelDiagram data-id="element-43" data-mp="2yicvikrm1uad" />
            <ol className="space-y-6" data-id="element-44" data-mp="2yjeah0bqz0xd">
              {FLYWHEEL.map((step, i) => <li key={step.name} className="grid grid-cols-[2.5rem_1fr] gap-5" data-id="element-45" data-mp="3i4oa71l93xs7" data-mpi={i}>
                  <span className={`text-body-sm ${step.pivot ? 'text-accent-text' : 'text-muted-foreground'}`} data-id="element-46" data-mp="3n2zi36rzjy7s">
                    0{i + 1}
                  </span>
                  <div data-id="element-47" data-mp="3d4jql2p1qmxr">
                    <b className="text-[11px] uppercase tracking-label" data-id="element-48" data-mp="3d40hmbhdj8vv">{step.name}</b>
                    <p className="mt-1 text-body-sm text-muted-foreground" data-id="element-49" data-mp="2zdex6ic9uqvv">{step.blurb}</p>
                  </div>
                </li>)}
              <li className="border-t border-border pt-8 text-body font-semibold lg:whitespace-nowrap lg:text-[clamp(0.95rem,1.35vw,1.15rem)]" data-id="element-50" data-mp="345eqvt1r0nk0">
                Every new connection expands what the whole network can do.
              </li>
            </ol>
          </div>
        </Section>

        {/* FOREST BAND ------------------------------------------------------- */}
        <section className="relative min-h-[560px] overflow-hidden" aria-labelledby="forest-heading" data-id="element-51" data-mp="2kg4gmukruuta">
          {/* Triptych backdrop: the same connected system at three scales —
              ground, grove, range. Hairline seams keep them legible as three
              photographs rather than one blurred field. */}
          <div className="absolute inset-0 grid grid-cols-3 gap-px bg-cream/20" data-id="element-52" data-mp="2qvwj25f4p9ey">
            <img src={SCENE_IMG.tundraSoil} alt="" className="h-full w-full object-cover object-center" data-id="element-53" data-mp="2lx31a0st6w8z" />
            <img src={SCENE_IMG.aspenGrove} alt="" className="h-full w-full object-cover object-center" data-id="element-54" data-mp="2d5sz5l3q1rso" />
            <img src={SCENE_IMG.windRiverLake} alt="" className="h-full w-full object-cover object-center" data-id="element-55" data-mp="2877xdh8tn024" />
          </div>
          {/* Neutral near-black wash rather than the forest-green one: the
              groundcover is red, and a green scrim muddies it. */}
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(90deg, rgba(12,10,9,0.68) 0%, rgba(12,10,9,0.4) 50%, rgba(12,10,9,0.58) 100%)'
        }} data-id="element-56" data-mp="1u91i7ibnjors" />
          <div className="section-shell relative grid min-h-[560px] items-center gap-12 py-20 text-cream lg:grid-cols-2" data-id="element-57" data-mp="ipb43o2so9vu">
            <div data-id="element-58" data-mp="nncihvp9zl0z">
              <p className="text-[11px] font-semibold uppercase tracking-overline" data-id="element-59" data-mp="nnw1lfqnwfgl">Built like nature</p>
              <h2 id="forest-heading" className="mt-6 font-serif text-h2" data-id="element-60" data-mp="3m4866vf5egt8">
                Nature does not
                <br data-id="element-61" data-mp="3r31jamqiw5sv" />
                work alone.
              </h2>
              <p className="mt-6 max-w-md text-body-sm leading-body" data-id="element-62" data-mp="3w2wbctm1b1fi">
                A forest thrives because everything is connected. Roots. Water. Fungi. Plants. Animals.
                Nutrients. Each makes the others stronger.
              </p>
            </div>
            <div className="lg:border-l lg:border-cream/40 lg:pl-16" data-id="element-63" data-mp="38e4p0iv4ldan">
              <p className="text-body-lg" data-id="element-64" data-mp="2f759n07a05e5">We are building innovation the same way.</p>
              <p className="mt-8 font-serif text-h2" data-id="element-65" data-mp="2f9hnp29wxvlx">
                Building
                <br data-id="element-66" data-mp="2k4dgi4mz4n03" />
                the soil.
              </p>
            </div>
          </div>
        </section>

        {/* AUDIENCE CARDS ---------------------------------------------------- */}
        <section className="border-b border-border" aria-label="Ways to get involved" data-id="element-67" data-mp="2u3mjfmzthbyl">
          <div className="grid md:grid-cols-3" data-id="element-68" data-mp="1wi6zisvknwxq">
            {AUDIENCE_CARDS.map(card => <article id={card.id} key={card.id} className="flex flex-col border-b border-border p-8 md:border-b-0 md:border-r last:md:border-r-0 lg:p-10" data-id="element-69" data-mp="21gia55toybh0">
                <p className="overline" data-id="element-70" data-mp="2b6uuawxlg2mm">{card.eyebrow}</p>
                <h3 className="mt-5 font-serif text-h3" data-id="element-71" data-mp="1dqxo2e28qy3i">{card.title}</h3>
                <p className="mt-4 text-body-sm leading-body text-muted-foreground" data-id="element-72" data-mp="1ioz7x17hr1g1">{card.body}</p>
              </article>)}
          </div>
          <div className="flex justify-center border-t border-border px-8 py-8" data-id="element-73" data-mp="3vyyywfacj17q">
            <Button href={CONTACT_HREF} data-id="element-74" data-mp="3ryym0z2way2s">Get in touch</Button>
          </div>
        </section>

        {/* PHILANTHROPY ------------------------------------------------------ */}
        <section id="support" className="theme-dark relative overflow-hidden bg-background text-foreground" aria-labelledby="support-heading" data-id="element-75" data-mp="3i093d436d9lh">
          <img src={SCENE_IMG.sunsetRidge} alt="" className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-76" data-mp="389xur48jqc89" />
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(90deg, rgba(12,10,9,0.9) 0%, rgba(12,10,9,0.72) 55%, rgba(12,10,9,0.6) 100%)'
        }} data-id="element-77" data-mp="348ktl7geex7o" />
          <div className="section-shell relative grid gap-8 py-10 lg:grid-cols-2 lg:items-center lg:py-12" data-id="element-78" data-mp="2pak16neqb3ia">
            <div data-id="element-79" data-mp="2fn6j7wso3zuj">
              <SectionHeading id="support-heading" eyebrow="Why Vitality" title={<>
                    Your gift does not
                    <br data-id="element-81" data-mp="26o30wv1broh3" />
                    fund one company.
                    <br data-id="element-82" data-mp="2ao3d090ybnkz" />
                    It multiplies all of them.
                  </>} data-id="element-80" data-mp="2fmoionyz2zgt" />

              {/* Attribution: the statement has a person behind it. */}
              <figure className="m-0 mt-8 flex items-center gap-6" data-id="element-83" data-mp="33cr4wng7hb7i">
                <img src={PORTRAIT.natalya} alt="Natalya Wallin" className="h-28 w-28 shrink-0 rounded-full object-cover object-top lg:h-32 lg:w-32" data-id="element-84" data-mp="33f4wgmbog5ab" />
                <figcaption data-id="element-85" data-mp="3hctgxt1u1aeu">
                  <p className="font-serif text-h4 leading-tight" data-id="element-86" data-mp="3hcjv8hntlpey">Natalya Wallin</p>
                  <p className="mt-1 overline text-muted-foreground" data-id="element-87" data-mp="3m41vf3i0rlyv">Chief Executive Officer</p>
                </figcaption>
              </figure>
            </div>

            <div data-id="element-88" data-mp="1amgj1hw4dzes">
              <p className="max-w-md text-body leading-body text-muted-foreground" data-id="element-89" data-mp="15pgdd836myo7">
                Investment capital helps companies grow. Philanthropy can fund what happens between them:
                the connections, collaborations and combinations no single company or investor owns.
              </p>
              <p className="mt-5 font-semibold" data-id="element-90" data-mp="rpfcvtqtxt99">That is where Vitality works.</p>

              <div className="mt-7" data-id="element-91" data-mp="mqlv3p4ifg3a">
                <Button disabled withArrow={false} aria-label="Support Vitality — coming soon" data-id="element-92" data-mp="mrnj0u6mjx2f">
                  Coming soon
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING BAND ------------------------------------------------------ */}
        <Section tone="deep" size="md" aria-labelledby="closing-heading" data-id="element-93" data-mp="2d1yweygsqilp">
          <div className="flex flex-col items-start gap-6" data-id="element-94" data-mp="284ghmrg3l95s">
            <p className="text-[11px] font-semibold uppercase tracking-overline" data-id="element-95" data-mp="2861bsfyi2q34">
              A healthier, more abundant world
            </p>
            <h2 id="closing-heading" className="max-w-2xl font-serif text-h2" data-id="element-96" data-mp="ml4o7ncf9ctc">
              The breakthrough we need
              <br data-id="element-97" data-mp="qmq8vuv76ve3" />
              may already exist.
            </h2>
            <p className="max-w-md text-body leading-body text-muted-foreground" data-id="element-98" data-mp="vn46eiz6amxv">
              It simply has not met the right idea, person or partner yet. Tell us what you are working
              on, or where you could help.
            </p>
            <Button href={CONTACT_HREF} data-id="element-99" data-mp="15j7k8m3e0a0n">Get in touch</Button>
          </div>
        </Section>
      </main>

      <SiteFooter data-id="element-100" data-mp="1acaekwfznnhw" />
    </div>;
}